import type { SalesProductInformation } from '../redux/backoffice/ordersSlice';
import { roundMoney } from './libs';

type SaleLineLike = {
  bp_order_price?: number | null;
  bp_order_quantity?: number | null;
  bp_order_mass?: number | null;
  bp_selling_price_changed?: boolean | null;
  bp_product_information?: {
    bp_product_price_type?: string | null;
    bp_product_selling_price?: number | null;
  } | null;
};

/**
 * Unit rate of a sale line, priced the way the server prices it
 * (getSaleProductSellingPrice / effectiveUnitPrice in the sales controller).
 * A bargained line is bp_order_price ÷ (mass|quantity), left unrounded so
 * rate × n gives the line total back (1000 ÷ 3 × 3 = 1000, not 999.99 as the
 * 2dp bp_product_selling_price would give). Every other line uses the
 * snapshot/catalog selling price the server returns in
 * bp_product_selling_price.
 */
export const saleLineUnitPrice = (line: SaleLineLike): number => {
  const pi = line.bp_product_information;
  const catalog = Number(pi?.bp_product_selling_price || 0);
  if (line.bp_selling_price_changed !== true) return catalog;
  const isUnit = pi?.bp_product_price_type === 'unit';
  const base = Number(isUnit ? line.bp_order_mass : line.bp_order_quantity);
  const total = Number(line.bp_order_price);
  if (!Number.isFinite(total) || total < 0 || !(base > 0)) return catalog;
  return total / base;
};

/**
 * The owner (back office) order endpoints return a slimmer sale line than the
 * POS endpoints: `bp_order_price` (the line total) is omitted entirely and
 * `bp_order_quantity` is omitted on unit-priced lines. The shared
 * Add-More-Products / Check-Out flow validates on both, so rebuild them before
 * handing back office lines to it: selling price × mass|quantity (as the Cart
 * builds a line), rounded to money precision the same way the server does.
 */
export const toPosSaleLine = (
  line: SalesProductInformation,
): SalesProductInformation => {
  const pi = line.bp_product_information;
  const hasPrice = typeof line.bp_order_price === 'number';
  const hasQuantity = typeof line.bp_order_quantity === 'number';
  if (hasPrice && hasQuantity) return line;

  const isUnit = pi?.bp_product_price_type === 'unit';
  const quantity = Number(line.bp_order_quantity ?? 0);
  const mass = Number(line.bp_order_mass ?? 0);
  const unitPrice = Number(pi?.bp_product_selling_price || 0);
  return {
    ...line,
    bp_order_quantity: quantity,
    bp_order_price: hasPrice
      ? line.bp_order_price
      : roundMoney(unitPrice * (isUnit ? mass : quantity)),
  };
};

export const toPosSaleLines = (
  lines: SalesProductInformation[] | null | undefined,
): SalesProductInformation[] => (lines ?? []).map(toPosSaleLine);
